/*
 * Purpose: Multitenancy — resolve workspaces for signed-in users
 * Last modified: 2026-06-28 | Agent
 * Completeness: 88
 */
export type TenantRole = 'owner' | 'admin' | 'member';

export interface AuthTenant {
  id: string;
  slug: string;
  name: string;
  role: TenantRole;
}

const DEMO_TENANTS: AuthTenant[] = [
  { id: 'tenant-personal', slug: 'personal', name: 'Personal workspace', role: 'owner' },
  { id: 'tenant-acme', slug: 'acme-corp', name: 'Acme Corp', role: 'admin' },
  { id: 'tenant-ops', slug: 'autblocks-ops', name: 'Autoblocks Ops', role: 'member' },
];

function slugFromEmail(email: string): string {
  const local = email.split('@')[0]?.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'workspace';
  return local.slice(0, 32);
}

function nameFromEmail(email: string): string {
  const local = email.split('@')[0] || 'you';
  return `${local.charAt(0).toUpperCase()}${local.slice(1)} workspace`;
}

/** Mock/dev: personal workspace + shared demo orgs for multitenancy UX. */
export function resolveTenantsForEmail(email: string): AuthTenant[] {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed) return [];

  const personal: AuthTenant = {
    id: `tenant-${slugFromEmail(trimmed)}`,
    slug: slugFromEmail(trimmed),
    name: nameFromEmail(trimmed),
    role: 'owner',
  };

  if (AUTH_MOCK_TENANTS()) {
    return [personal, ...DEMO_TENANTS.filter((t) => t.id !== personal.id)];
  }

  return [personal];
}

function AUTH_MOCK_TENANTS(): boolean {
  return import.meta.env.VITE_AUTH_MOCK === '1';
}

export function getTenantById(tenants: AuthTenant[], id: string | null): AuthTenant | null {
  if (!id) return null;
  return tenants.find((t) => t.id === id) ?? null;
}

export function tenantStoragePrefix(tenantId: string): string {
  return `autoblocks:${tenantId}:`;
}
